import React, { memo, useEffect, useState } from 'react';
import { StyledReservationResults } from './styled';
import { InputField, Text } from '~/shared/components';
import { useDebounce } from 'react-use';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from '~/shared/utils';
import Search from '$icons/search.svg';
import { ProductViewModel } from '~/lib/data-contract';
import { useStoresWithStock } from '~/features/stores';
import { AnimatePresence } from 'framer-motion';
import { useTheme } from '@emotion/react';
import { Spacer } from '~/theme';
import { UseStoresWithStockProps } from '~/features/stores/hooks/useStoresWithStock';
import { useAccount } from '~/features/club';
import { StyledPendingIndicator } from '../../styled';
import { ReserveReceiptStore } from '../ReserveReceiptStore/ReserveReceiptStore';
import { ReserveStores } from '../ReserveStores/ReserveStores';

type ReserveSectionStoresProps = {
    product: ProductViewModel;
    isReceipt: boolean;
};

const heightVariants = {
    initial: {
        height: '0px',
    },
    enter: {
        height: 'auto',
    },
};

export const ReserveSectionStores = memo(({ product, isReceipt }: ReserveSectionStoresProps) => {
    const { translate } = useTranslation();
    const { animations } = useTheme();
    const { account } = useAccount();
    const zipCode = account?.address?.zipCode;
    const { productColorId } = product;
    const [searchInputValue, setSearchInputValue] = useState(zipCode || '');
    const [initialValueSet, setInitialValueSet] = useState(false);
    const {
        resetField,
        watch,
        getValues,
        formState: { submitCount },
    } = useFormContext();

    const selectedStoreId = watch('storeId');
    const activeVariantId = watch('variantId');

    const [searchProps, setSearchProps] = useState<UseStoresWithStockProps>({
        searchTerm: searchInputValue,
    });

    // If no searchInputValue we do a default wildcard search for all stores
    const searchParams = searchInputValue
        ? {}
        : {
              searchParams: {
                  Radius: 10_000_000,
                  ZipCode: '0',
              },
          };

    const {
        data: { stores },
        isFetching,
    } = useStoresWithStock({
        ...searchParams,
        searchTerm: searchProps.searchTerm,
        slugs: {
            productColorId,
            variantId: activeVariantId,
        },
    });

    useEffect(() => {
        const defaultSearchValue = zipCode;
        if (defaultSearchValue && !initialValueSet) {
            setSearchInputValue(defaultSearchValue);
            setInitialValueSet(true);
        }
    }, [zipCode, initialValueSet, setInitialValueSet]);

    const storeIdInvalid = submitCount > 0 && !getValues('storeId');

    useDebounce(() => setSearchProps({ searchTerm: searchInputValue }), 350, [searchInputValue]);

    useEffect(() => {
        if (
            stores?.some((store) => store.id === selectedStoreId) ||
            stores?.some((store) => store.id === account?.storeId)
        ) {
            return;
        }
        resetField('storeId');
    }, [stores, selectedStoreId, resetField, account?.storeId]);

    if (isReceipt) {
        const selectedStore = stores?.find((store) => store.id === selectedStoreId);
        return <ReserveReceiptStore pickupStore={selectedStore} />;
    }

    return (
        <div>
            <Text as="h2" variant="display4">
                {translate('reservation.stores.heading')}
            </Text>
            <Spacer space="1" />
            {stores && stores.length > 0 ? (
                <Text variant="bodySm">
                    {translate('reservation.stores.subheadingWithCount', { count: stores.length })}
                </Text>
            ) : (
                <Text variant="bodySm">{translate('reservation.stores.subheadingHelpText')}</Text>
            )}
            <Spacer space="3" />
            <InputField
                label={translate('reservation.searchInput.label')}
                placeholder={translate('reservation.searchInput.placeholder')}
                helpText={translate('reservation.searchInput.helpText')}
                isInvalid={storeIdInvalid}
                invalidMessage={
                    storeIdInvalid ? translate('reservation.searchInput.invalid') : undefined
                }
                leadingIcon={
                    <StyledPendingIndicator isFetching={isFetching} children={<Search />} />
                }
                onChange={(e) => setSearchInputValue(e.target.value)}
                name="Reservation stores input"
                value={searchInputValue}
                type="text"
            />
            <Spacer space="2" />
            <StyledPendingIndicator isFetching={isFetching}>
                <AnimatePresence>
                    {stores && (
                        <StyledReservationResults
                            initial="initial"
                            animate="enter"
                            exit="initial"
                            variants={heightVariants}
                            transition={{
                                ...animations.springDefault,
                                layout: {
                                    duration: animations.durations.moderate01,
                                    ease: animations.bezierValues.standard.productive,
                                },
                            }}
                            hasResults={stores.length > 0}
                        >
                            {stores.length ? (
                                <ReserveStores
                                    stores={stores}
                                    isFetching={isFetching}
                                    account={account}
                                />
                            ) : (
                                <Text>{translate('reservation.stores.noResults')}</Text>
                            )}
                        </StyledReservationResults>
                    )}
                </AnimatePresence>
            </StyledPendingIndicator>
        </div>
    );
});
